import React, { useRef, Suspense } from 'react';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';
import debounce from 'lodash/debounce';

import { WidthObserver } from '@atlaskit/width-detector';

import { LoadableAfterPaint } from '@confluence/loadable';
import { BannerStateContainer } from '@confluence/banners/entry-points/BannerStateContainer.ts';
import { isStableUIEnabledForTests } from '@confluence/e2e-url-override/entry-points/isStableUIEnabledForTests.ts';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { ErrorBoundary } from '@confluence/error-boundary/entry-points/ErrorBoundary.ts';
import { expVal } from '@confluence/feature-experiments';
import { useRenderServerPlaceholder } from '@confluence/ssr-utilities/entry-points/useRenderServerPlaceholder.ts';

import { useCookiesConsentBanner } from './useCookiesConsentBanner.ts';
import { MIN_BANNER_HEIGHT } from './constants.ts';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Banner = styled.div({
	display: 'flex',
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const BannerContent = styled.div({
	flex: 1,
});

const ConsentBannerComponent = LoadableAfterPaint({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-consent-banner-wrapper" */ './ConsentBannerWrapper.tsx'
			)
		).ConsentBannerWrapper,
});

const CookieConsentBannerComponentInSuspense = ({ children }: React.PropsWithChildren<{}>) => {
	if (expVal('confluence_cookie_consent_banner_suspense', 'isEnabled', false)) {
		return <Suspense fallback={null}>{children}</Suspense>;
	}
	return children;
};

type CookiesConsentBannerProps = {};

type CookiesConsentBannerComponentProps = {
	bannerState: BannerStateContainer;
};

const CookiesConsentBannerComponent: React.FC<CookiesConsentBannerComponentProps> = ({
	bannerState,
}) => {
	const bannerRef = useRef<HTMLDivElement | null>(null);
	const { onWidthChange, showBanner, hideBanner, prefetchedData } = useCookiesConsentBanner(
		bannerState,
		bannerRef,
	);
	const renderServerPlaceholder = useRenderServerPlaceholder();

	const onResize = debounce(onWidthChange, 50);

	return (
		<Banner
			ref={bannerRef}
			role="banner"
			data-testid="cookie-consent-banner"
			data-vc="cookie-consent-banner"
		>
			<BannerContent>
				<WidthObserver setWidth={onResize} />
				<ConsentBannerComponent
					product="Confluence"
					onShow={showBanner}
					onHide={hideBanner}
					initialPreferences={prefetchedData}
					newMinHeight={MIN_BANNER_HEIGHT}
					ssrOptions={{
						isSSR: renderServerPlaceholder,
					}}
				/>
			</BannerContent>
		</Banner>
	);
};

export const CookiesConsentBanner: React.FC<CookiesConsentBannerProps> = () => {
	if (isStableUIEnabledForTests()) {
		return null;
	}

	return (
		<ErrorBoundary attribution={Attribution.BACKBONE}>
			<Subscribe to={[BannerStateContainer]}>
				{(bannerState: BannerStateContainer) => (
					<CookieConsentBannerComponentInSuspense>
						<CookiesConsentBannerComponent bannerState={bannerState} />
					</CookieConsentBannerComponentInSuspense>
				)}
			</Subscribe>
		</ErrorBoundary>
	);
};
